import React, { useState, useEffect } from "react";
import {
  Percent,
  Search,
  Plus,
  Edit3,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Building,
  DollarSign,
} from "lucide-react";
import { ClientService } from "../../services/clients/clientService";
import { toast } from "react-hot-toast";

interface Commission {
  id: string;
  client: string;
  client_nom: string;
  nom: string;
  type_commission: "pourcentage" | "fixe";
  valeur: string;
  description: string;
  est_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Client {
  id: string;
  raison_sociale: string;
}

interface CommissionForm {
  nom: string;
  type_commission: "pourcentage" | "fixe";
  valeur: string;
  description: string;
}

const emptyForm: CommissionForm = {
  nom: "",
  type_commission: "pourcentage",
  valeur: "",
  description: "",
};

export const AdminCommissions: React.FC = () => {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClientId, setFilterClientId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState<Commission | null>(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [form, setForm] = useState<CommissionForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [commRes, clientsRes] = await Promise.all([
        ClientService.getAllCommissions(),
        ClientService.getClients(),
      ]);
      setCommissions(commRes.data || []);
      setClients(clientsRes.data.results || clientsRes.data || []);
    } catch (error) {
      console.error("Erreur chargement données", error);
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenAdd = () => {
    setEditingCommission(null);
    setForm(emptyForm);
    setSelectedClientId(filterClientId || "");
    setShowModal(true);
  };

  const handleOpenEdit = (commission: Commission) => {
    setEditingCommission(commission);
    setForm({
      nom: commission.nom,
      type_commission: commission.type_commission,
      valeur: commission.valeur,
      description: commission.description,
    });
    setSelectedClientId(commission.client);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId && !editingCommission) {
      toast.error("Veuillez sélectionner un client");
      return;
    }
    if (!form.nom || !form.valeur) {
      toast.error("Le nom et la valeur sont obligatoires");
      return;
    }

    try {
      setSubmitting(true);
      if (editingCommission) {
        await ClientService.updateCommission(editingCommission.id, form);
        toast.success("Commission modifiée avec succès");
      } else {
        await ClientService.createCommission(selectedClientId, form);
        toast.success("Commission ajoutée avec succès");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Erreur sauvegarde commission", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (commission: Commission) => {
    try {
      await ClientService.updateCommission(commission.id, {
        est_active: !commission.est_active,
      });
      toast.success(
        commission.est_active ? "Commission désactivée" : "Commission activée"
      );
      fetchData();
    } catch (error) {
      console.error("Erreur toggle commission", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async (commission: Commission) => {
    if (!window.confirm(`Supprimer la commission "${commission.nom}" ?`)) return;
    try {
      await ClientService.deleteCommission(commission.id);
      toast.success("Commission supprimée");
      fetchData();
    } catch (error) {
      console.error("Erreur suppression commission", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredCommissions = commissions.filter((c) => {
    const matchesSearch =
      c.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.client_nom.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = filterClientId
      ? String(c.client) === filterClientId
      : true;
    return matchesSearch && matchesClient;
  });

  if (loading && commissions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des Frais & Commissions
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Configurez les commissions appliquées aux fournisseurs de chaque client
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleOpenAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle commission
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-primary-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Percent className="h-8 w-8 text-primary-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total commissions</p>
              <p className="text-2xl font-bold text-gray-900">{commissions.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ToggleRight className="h-8 w-8 text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Actives</p>
              <p className="text-2xl font-bold text-gray-900">
                {commissions.filter((c) => c.est_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-orange-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Building className="h-8 w-8 text-orange-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Clients configurés</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(commissions.map((c) => c.client)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Rechercher par nom ou client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="sm:w-64">
              <select
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filterClientId}
                onChange={(e) => setFilterClientId(e.target.value)}
              >
                <option value="">Tous les clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.raison_sociale}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valeur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCommissions.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <Percent className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm">Aucune commission configurée</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Cliquez sur "Nouvelle commission" pour commencer
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCommissions.map((commission) => (
                  <tr
                    key={commission.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <Building className="h-4 w-4 text-primary-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {commission.client_nom}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {commission.nom}
                        </p>
                        {commission.description && (
                          <p className="text-xs text-gray-500 truncate max-w-xs">
                            {commission.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          commission.type_commission === "pourcentage"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {commission.type_commission === "pourcentage" ? (
                          <Percent className="h-3 w-3 mr-1" />
                        ) : (
                          <DollarSign className="h-3 w-3 mr-1" />
                        )}
                        {commission.type_commission === "pourcentage"
                          ? "Pourcentage"
                          : "Montant fixe"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {commission.type_commission === "pourcentage"
                          ? `${commission.valeur}%`
                          : `${Number(commission.valeur).toLocaleString("fr-FR")} XOF`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(commission)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                          commission.est_active
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {commission.est_active ? (
                          <>
                            <ToggleRight className="h-3 w-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="h-3 w-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleOpenEdit(commission)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Modifier"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(commission)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowModal(false)}
            />
            <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-6 py-5">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingCommission
                        ? "Modifier la commission"
                        : "Nouvelle commission"}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Client Selection */}
                    {!editingCommission && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Client *
                        </label>
                        <select
                          className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={selectedClientId}
                          onChange={(e) => setSelectedClientId(e.target.value)}
                          required
                        >
                          <option value="">Sélectionner un client</option>
                          {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.raison_sociale}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {editingCommission && (
                      <div className="bg-gray-50 rounded-md p-3">
                        <p className="text-xs text-gray-500">Client</p>
                        <p className="text-sm font-medium text-gray-900">
                          {editingCommission.client_nom}
                        </p>
                      </div>
                    )}

                    {/* Nom */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom de la commission *
                      </label>
                      <input
                        type="text"
                        className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Ex: Commission de succès"
                        value={form.nom}
                        onChange={(e) =>
                          setForm({ ...form, nom: e.target.value })
                        }
                        required
                      />
                    </div>

                    {/* Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="type_commission"
                            value="pourcentage"
                            checked={form.type_commission === "pourcentage"}
                            onChange={() =>
                              setForm({
                                ...form,
                                type_commission: "pourcentage",
                              })
                            }
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Pourcentage (%)
                          </span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="type_commission"
                            value="fixe"
                            checked={form.type_commission === "fixe"}
                            onChange={() =>
                              setForm({ ...form, type_commission: "fixe" })
                            }
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Montant fixe (XOF)
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Valeur */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valeur *
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="block w-full py-2 px-3 pr-12 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder={
                            form.type_commission === "pourcentage"
                              ? "Ex: 2.5"
                              : "Ex: 50000"
                          }
                          value={form.valeur}
                          onChange={(e) =>
                            setForm({ ...form, valeur: e.target.value })
                          }
                          required
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">
                            {form.type_commission === "pourcentage"
                              ? "%"
                              : "XOF"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Description optionnelle..."
                        value={form.description}
                        onChange={(e) =>
                          setForm({ ...form, description: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enregistrement...
                      </>
                    ) : editingCommission ? (
                      "Modifier"
                    ) : (
                      "Ajouter"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
